import MaterialSkuRequests from '../components/MaterialSkuRequests';
import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const TYPE_COLORS = {
  raw:      { bg:'#EDF4FF', color:'#1E4FAD', label:'Raw' },
  prepared: { bg:'#E8F5EB', color:'#2E6B3A', label:'Prepared' },
  wip:      { bg:'#FFF3DC', color:'#8B5E0A', label:'WIP' },
  finished: { bg:'#F2EFE7', color:'#556B2F', label:'Finished' },
};

const INVENTORY_TYPES = ['prepared','wip','finished'];

export default function MaterialSKUs() {
  const { user } = useAuth();
  const canWrite = ['super_admin','admin','inventory_officer'].includes(user?.role);

  // SUPER ADMIN CHILD SKU DELETE CONTROL
  const canDeleteChildSKU =
    user?.role === 'super_admin';

  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showLowOnly, setShowLowOnly] = useState(false);
  const [deletingSkuId, setDeletingSkuId] =
    useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/inventory-control/skus'),
      get('/materials')
    ]).then(([s, m]) => {
      setSkus(s.data || []);
      setMaterials(m.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const generateSKU = (material_id, inventory_type, colour, size_spec) => {
    const mat = materials.find(m => m.id === material_id);
    if (!mat) return '';
    const base = mat.name.toUpperCase()
      .replace(/[^A-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 12)
      .replace(/-$/, '');
    const col = colour ? '-' + colour.toUpperCase().replace(/[^A-Z0-9]/g,'').substring(0,4) : '';
    const size = size_spec ? '-' + size_spec.toUpperCase().replace(/[^A-Z0-9]/g,'').substring(0,6) : '';
    const type =
      inventory_type === 'prepared'
        ? '-PREP'
        : inventory_type === 'wip'
          ? '-WIP'
          : '-FG';
    return base + col + size + type;
  };

  const filtered = skus.filter(s => {
    const matchSearch = !search ||
      s.sku?.toLowerCase().includes(search.toLowerCase()) ||
      s.description?.toLowerCase().includes(search.toLowerCase()) ||
      s.material_name?.toLowerCase().includes(search.toLowerCase());
    const matchType = !typeFilter || s.inventory_type === typeFilter;
    const matchLow = !showLowOnly || parseFloat(s.current_balance||0) <= parseFloat(s.reorder_level||0);
    return matchSearch && matchType && matchLow;
  });

  const updateModalSKU = (updates, current) => {
    const merged = { ...current, ...updates };
    if (!merged.id) { // only auto-generate for new SKUs
      merged.sku = generateSKU(merged.material_id, merged.inventory_type, merged.colour, merged.size_spec);
    }
    return merged;
  };

  const openModal = (sku=null) => {
    setError('');
    setModal(sku ? { ...sku } : {
      material_id:'', sku:'', description:'', inventory_type:'prepared',
      unit_of_measure:'', colour:'', size_spec:'', storage_location:'',
      reorder_level:0, target_stock_level:0
    });
  };

  const save = async () => {
    setError('');
    if (!modal.material_id || !modal.sku || !modal.description || !modal.inventory_type || !modal.unit_of_measure) {
      setError('Material, SKU, description, type and unit are required'); return;
    }
    setSaving(true);
    try {
      if (modal.id) {
        await put('/inventory-control/skus/'+modal.id, modal);
      } else {
        await post('/inventory-control/skus', modal);
      }
      setModal(null);
      load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const deleteChildSKU = async sku => {
    if (!canDeleteChildSKU) {
      alert(
        'Only Super Admin can delete a child SKU.'
      );
      return;
    }

    const confirmation = window.prompt(
      'Delete child SKU "' +
        sku.sku +
        '"?\n\n' +
        'Parent material: ' +
        (sku.material_name || 'Unknown') +
        '\n\n' +
        'This deletes only the child SKU. ' +
        'The parent material will not be deleted.\n\n' +
        'Type DELETE to continue.'
    );

    if (confirmation !== 'DELETE') {
      return;
    }

    setDeletingSkuId(sku.id);

    try {
      const response = await del(
        '/inventory-control/skus/' + sku.id
      );

      alert(
        response.message ||
        'Child SKU deleted successfully.'
      );

      await load();
    } catch (error) {
      alert(
        error.message ||
        'The child SKU could not be deleted.'
      );
    } finally {
      setDeletingSkuId(null);
    }
  };

  // Summary counts
  const summary = INVENTORY_TYPES.map(t => ({
    type: t,
    count: skus.filter(s => s.inventory_type === t).length,
    low: skus.filter(s => s.inventory_type === t && parseFloat(s.current_balance||0) <= parseFloat(s.reorder_level||0) && parseFloat(s.reorder_level||0) > 0).length
  }));

  return (
    <div>
      {['super_admin','inventory_officer'].includes(user?.role)&&<MaterialSkuRequests user={user}/>} 
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Material SKUs</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Prepared, WIP and finished child-SKU register
          </p>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => openModal()}>+ Add SKU</button>
        )}
      </div>

      {/* Summary tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20}}>
        {summary.map(s => (
          <div key={s.type} onClick={() => setTypeFilter(typeFilter===s.type?'':s.type)}
            style={{background:typeFilter===s.type?TYPE_COLORS[s.type].bg:'#fff',
              border:`1px solid ${TYPE_COLORS[s.type].color}44`,borderRadius:10,
              padding:'12px 16px',cursor:'pointer',transition:'all .15s'}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',
              color:TYPE_COLORS[s.type].color,marginBottom:4}}>{TYPE_COLORS[s.type].label}</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',fontWeight:700,
              color:TYPE_COLORS[s.type].color}}>{s.count}</div>
            {s.low > 0 && (
              <div style={{fontSize:10,color:'#8F2E24',marginTop:2}}>⚠ {s.low} low stock</div>
            )}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="search-bar" style={{marginBottom:16}}>
        <input placeholder="Search SKU, description or material..."
          value={search} onChange={e=>setSearch(e.target.value)}
          style={{flex:1,minWidth:220}} />
        <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
          className="form-control" style={{width:'auto'}}>
          <option value="">All Types</option>
          {INVENTORY_TYPES.map(t=>(
            <option key={t} value={t}>{TYPE_COLORS[t].label}</option>
          ))}
        </select>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,cursor:'pointer',whiteSpace:'nowrap'}}>
          <input type="checkbox" checked={showLowOnly} onChange={e=>setShowLowOnly(e.target.checked)}
            style={{accentColor:'#8F2E24'}} />
          Low stock only
        </label>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? <div className="spinner"/> : (
          <table>
            <thead><tr style={{background:'#556B2F'}}>
              <th style={{color:'#F2EFE7'}}>SKU</th>
              <th style={{color:'#F2EFE7'}}>Description</th>
              <th style={{color:'#F2EFE7'}}>Material</th>
              <th style={{color:'#F2EFE7'}}>Type</th>
              <th style={{color:'#F2EFE7'}}>Unit</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Balance</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Reserved</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Available</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Reorder</th>
              <th style={{textAlign:'right',color:'#F2EFE7'}}>Target</th>
              <th style={{color:'#F2EFE7'}}>Location</th>
              {canWrite && <th></th>}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="12">
                  <div className="empty-state"><h3>No SKUs found</h3>
                    {canWrite && <button className="btn btn-primary" onClick={()=>openModal()}>Add First SKU</button>}
                  </div>
                </td></tr>
              ) : filtered.map(s => {
                const isLow = parseFloat(s.current_balance||0) <= parseFloat(s.reorder_level||0) && parseFloat(s.reorder_level||0) > 0;
                const available = parseFloat(s.available_balance||0);
                const tc = TYPE_COLORS[s.inventory_type] || TYPE_COLORS.raw;
                return (
                  <tr key={s.id} style={{background:isLow?'#fffbf0':''}}>
                    <td>
                      <div style={{fontWeight:700,fontSize:12,fontFamily:'monospace',color:'#3B2A21'}}>{s.sku}</div>
                      {isLow && <span className="badge b-part" style={{fontSize:8,marginTop:2}}>Low Stock</span>}
                    </td>
                    <td style={{fontSize:13}}>{s.description}</td>
                    <td style={{fontSize:12,color:'#8A8A8A'}}>{s.material_name}</td>
                    <td>
                      <span style={{background:tc.bg,color:tc.color,fontSize:9,fontWeight:700,
                        padding:'2px 8px',borderRadius:20,textTransform:'uppercase'}}>
                        {tc.label}
                      </span>
                    </td>
                    <td style={{fontSize:12}}>{s.unit_of_measure}</td>
                    <td className="amt" style={{color:isLow?'#8F2E24':'inherit',fontWeight:isLow?700:400}}>
                      {Number(s.current_balance||0).toLocaleString()}
                    </td>
                    <td className="amt" style={{fontSize:12,color:'#8B5E0A'}}>
                      {Number(s.reserved_quantity||0).toLocaleString()}
                    </td>
                    <td className="amt" style={{color:available<=0?'#8F2E24':'#2E6B3A',fontWeight:600}}>
                      {Number(available).toLocaleString()}
                    </td>
                    <td className="amt" style={{fontSize:12}}>{Number(s.reorder_level||0).toLocaleString()}</td>
                    <td className="amt" style={{fontSize:12}}>{Number(s.target_stock_level||0).toLocaleString()}</td>
                    <td style={{fontSize:11,color:'#8A8A8A'}}>{s.storage_location||'—'}</td>
                    {canWrite && (
                      <td>
                        <div
                          style={{
                            display:'flex',
                            gap:6,
                            alignItems:'center',
                            justifyContent:'flex-end'
                          }}
                        >
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={()=>openModal(s)}
                          >
                            Edit
                          </button>

                          {canDeleteChildSKU && (
                            <button
                              className="btn btn-outline btn-sm"
                              disabled={
                                deletingSkuId === s.id
                              }
                              onClick={() =>
                                deleteChildSKU(s)
                              }
                              style={{
                                color:'#8F2E24',
                                borderColor:'#8F2E24'
                              }}
                              title={
                                'Delete this child SKU only'
                              }
                            >
                              {deletingSkuId === s.id
                                ? 'Deleting...'
                                : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-hdr">
              <span className="modal-title">{modal.id ? 'Edit SKU' : 'Add Material SKU'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Parent Material *</label>
                  <select className="form-control" value={modal.material_id||''}
                    onChange={e=>setModal(updateModalSKU({material_id:e.target.value},modal))}>
                    <option value="">Select material...</option>
                    {materials.map(m=>(
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Inventory Type *</label>
                  <select className="form-control" value={modal.inventory_type||'prepared'}
                    onChange={e=>setModal(updateModalSKU({inventory_type:e.target.value},modal))}>
                    {INVENTORY_TYPES.map(t=>(
                      <option key={t} value={t}>{TYPE_COLORS[t].label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">SKU Code *</label>
                  <input className="form-control" value={modal.sku||''}
                    onChange={e=>setModal({...modal,sku:e.target.value.toUpperCase()})}
                    placeholder="Auto-generated — you can edit" />
                  <div className="form-hint">Auto-generated from material, type, colour and size. Edit if needed.</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Unit of Measure *</label>
                  <input className="form-control" value={modal.unit_of_measure||''}
                    onChange={e=>setModal({...modal,unit_of_measure:e.target.value})}
                    placeholder="e.g. Roll, Piece, Set" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description *</label>
                <input className="form-control" value={modal.description||''}
                  onChange={e=>setModal({...modal,description:e.target.value})}
                  placeholder="e.g. Black Webbing 26-inch Piece" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Colour</label>
                  <input className="form-control" value={modal.colour||''}
                    onChange={e=>setModal(updateModalSKU({colour:e.target.value},modal))}
                    placeholder="e.g. Black, Natural" />
                </div>
                <div className="form-group">
                  <label className="form-label">Size / Specification</label>
                  <input className="form-control" value={modal.size_spec||''}
                    onChange={e=>setModal(updateModalSKU({size_spec:e.target.value},modal))}
                    placeholder="e.g. 26 inches, 2cm diameter" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Storage Location</label>
                <input className="form-control" value={modal.storage_location||''}
                  onChange={e=>setModal({...modal,storage_location:e.target.value})}
                  placeholder="e.g. Shelf A3, Raw Materials Store" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Reorder Level</label>
                  <input type="number" className="form-control" value={modal.reorder_level||0}
                    onChange={e=>setModal({...modal,reorder_level:e.target.value})} min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Target Stock Level</label>
                  <input type="number" className="form-control" value={modal.target_stock_level||0}
                    onChange={e=>setModal({...modal,target_stock_level:e.target.value})} min="0" />
                </div>
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':modal.id?'Save Changes':'Add SKU'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
